import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaUpload, FaCheckCircle, FaBook } from 'react-icons/fa';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const AdminAIKnowledge = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ai/knowledge/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error al obtener estado:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Error al obtener estado' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setMessage(null);
    } else {
      setSelectedFile(null);
      setMessage({ type: 'error', text: 'Solo se permiten archivos PDF' });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setMessage(null);

      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/ai/knowledge/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage({ type: 'success', text: response.data.message });
      setSelectedFile(null);
      
      // Resetear input
      const fileInput = document.getElementById('pdfInput');
      if (fileInput) fileInput.value = '';

      // Actualizar estado
      await fetchStatus();

    } catch (error) {
      console.error('Error al subir archivo:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Error al subir el archivo' 
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <FaBook className="me-2" />
          Gestión de Conocimiento del Asistente IA
        </h5>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Cargando estado...</p>
          </div>
        ) : (
          <>
            {/* Estado actual */}
            {status && status.configured && (
              <Alert variant="success" className="mb-4">
                <div className="d-flex align-items-center">
                  <FaCheckCircle size={24} className="me-3" />
                  <div>
                    <strong>Asistente configurado</strong>
                    <div className="small mt-1">
                      Archivos cargados: <Badge bg="primary">{status.filesCount}</Badge>
                      {status.lastUpdated && (
                        <span className="ms-2">
                          Última actualización: {new Date(status.lastUpdated).toLocaleString('es-AR')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Alert>
            )}

            {status && !status.configured && (
              <Alert variant="warning">
                <strong>No hay manual configurado</strong>
                <p className="mb-0 small">Sube el PDF del manual de usuario para activar el asistente</p>
              </Alert>
            )}

            {/* Mensajes */}
            {message && (
              <Alert 
                variant={message.type === 'success' ? 'success' : 'danger'}
                dismissible
                onClose={() => setMessage(null)}
              >
                {message.text}
              </Alert>
            )}

            {/* Upload */}
            <div className="border rounded p-4 bg-light">
              <h6 className="mb-3">Subir / Actualizar Manual</h6>
              
              <div className="mb-3">
                <input
                  id="pdfInput"
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  className="form-control"
                  disabled={uploading}
                />
                <small className="text-muted">
                  Solo archivos PDF. Máximo 50MB.
                </small>
              </div>

              {selectedFile && (
                <Alert variant="info" className="mb-3">
                  <strong>Archivo seleccionado:</strong> {selectedFile.name}
                  <br />
                  <small>Tamaño: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</small>
                </Alert>
              )}

              <Button
                variant="primary"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
              >
                {uploading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Procesando...
                  </>
                ) : (
                  <>
                    <FaUpload className="me-2" />
                    Subir Manual
                  </>
                )}
              </Button>

              <div className="mt-3 small text-muted">
                <strong>Nota:</strong> El procesamiento puede tardar unos minutos dependiendo del tamaño del archivo.
                El archivo se indexará automáticamente para búsqueda semántica.
              </div>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default AdminAIKnowledge;
